import React from 'react';
import { motion } from 'framer-motion';
import {
    BeakerIcon,
    BugAntIcon,
    CloudIcon,
    CameraIcon,
    SunIcon,
    ChartBarIcon
} from '@heroicons/react/24/outline';
import { CheckCircleIcon } from '@heroicons/react/24/solid';

const FarmgetApp = () => {
    const fadeInUp = {
        hidden: { opacity: 0, y: 30 },
        visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: "easeOut" } }
    };

    const features = [
        {
            title: "Soil Health Analysis",
            desc: "5 key parameters (N, P, K, Organic Carbon, pH)",
            icon: BeakerIcon
        },
        {
            title: "Pest & Disease Forewarning",
            desc: "Early risk alerts",
            icon: BugAntIcon
        },
        {
            title: "Smart Irrigation Management",
            desc: "Moisture & weather-based scheduling",
            icon: CloudIcon
        },
        {
            title: "Image-Based Advisory",
            desc: "Instant crop diagnosis",
            icon: CameraIcon
        },
        {
            title: "Weather Forecast Integration",
            desc: "Better field planning",
            icon: SunIcon
        },
        {
            title: "Complete Crop Management",
            desc: "INM, IPM, IDM & IWM guidance",
            icon: ChartBarIcon
        }
    ];

    return (
        <div className="bg-gray-50 min-h-screen font-sans">
            {/* Hero Section */}
            <section className="relative h-[60vh] min-h-[400px] flex items-center justify-center bg-gray-900 text-white overflow-hidden">
                <motion.div
                    initial={{ scale: 1.1 }}
                    animate={{ scale: 1 }}
                    transition={{ duration: 10, repeat: Infinity, repeatType: "reverse" }}
                    className="absolute inset-0 w-full h-full"
                >
                    <div className="absolute inset-0 bg-black/60 z-10"></div>
                    <img
                        src="https://images.unsplash.com/photo-1586771107445-d3ca888129ff?ixlib=rb-4.0.3&auto=format&fit=crop&w=1744&q=80"
                        alt="FarmGet App"
                        className="w-full h-full object-cover opacity-80"
                    />
                </motion.div>

                <div className="relative z-20 text-center px-4 max-w-5xl mx-auto">
                    <motion.h1
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.2 }}
                        className="text-4xl md:text-6xl font-bold mb-6 leading-tight"
                    >
                        <span className="text-transparent bg-clip-text bg-gradient-to-r from-green-400 to-emerald-500">FarmGet App</span>
                    </motion.h1>
                    <motion.p
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.4 }}
                        className="text-xl md:text-2xl text-gray-200 font-light"
                    >
                        Smart Farming. Simplified.
                    </motion.p>
                </div>
            </section>

            {/* Content Section */}
            <section className="py-20 px-4 md:px-8 max-w-7xl mx-auto">
                <div className="flex flex-col lg:flex-row gap-16 items-start">
                    <motion.div
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true }}
                        variants={fadeInUp}
                        className="w-full"
                    >
                        <div className="max-w-3xl mx-auto text-center mb-16">
                            <p className="text-gray-600 text-xl md:text-2xl leading-relaxed mb-6 font-light">
                                <strong className="text-gray-900 font-bold">FarmGet</strong> is an all-in-one digital agriculture platform designed to turn farm data into clear, actionable decisions.
                            </p>
                            <p className="text-gray-600 text-lg leading-relaxed mb-8 font-light max-w-2xl mx-auto">
                                It empowers farmers with real-time intelligence to improve yield, reduce risk, and maximize profitability.
                            </p>
                        </div>

                        {/* What FarmGet Delivers */}
                        <div className="mb-20">
                            <h2 className="text-3xl font-bold text-center text-gray-900 mb-12 flex items-center justify-center gap-3">
                                What FarmGet Delivers
                            </h2>
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                                {features.map((feature, idx) => (
                                    <motion.div
                                        key={idx}
                                        initial="hidden"
                                        whileInView="visible"
                                        viewport={{ once: true }}
                                        variants={fadeInUp}
                                        transition={{ delay: idx * 0.1 }}
                                        className="bg-white p-8 rounded-2xl shadow-lg border border-gray-100 hover:border-green-300 hover:shadow-xl transition-all group"
                                    >
                                        <div className="bg-green-50 w-14 h-14 rounded-xl flex items-center justify-center mb-6 text-green-600 group-hover:bg-green-600 group-hover:text-white transition-colors duration-300">
                                            <feature.icon className="w-8 h-8" />
                                        </div>
                                        <h3 className="text-xl font-bold text-gray-900 mb-2">{feature.title}</h3>
                                        <div className="flex items-start gap-2">
                                            <CheckCircleIcon className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                                            <p className="text-gray-600 font-medium">{feature.desc}</p>
                                        </div>
                                    </motion.div>
                                ))}
                            </div>
                        </div>

                        {/* Why FarmGet? */}
                        <div className="bg-gray-900 p-10 md:p-16 rounded-3xl shadow-2xl text-white relative overflow-hidden">
                            <div className="absolute -right-20 -top-20 w-80 h-80 bg-green-500/20 rounded-full blur-3xl text-white"></div>

                            <h3 className="text-3xl md:text-4xl font-bold mb-10 flex items-center gap-3 relative z-10 text-emerald-400">
                                Why FarmGet?
                            </h3>

                            <div className="grid grid-cols-1 md:grid-cols-3 gap-10 relative z-10 mb-12 border-b border-gray-800 pb-12">
                                <div className="md:col-span-1">
                                    <p className="text-2xl font-light text-gray-300 leading-relaxed space-y-2">
                                        <span className="block line-through text-gray-600 mb-2 decoration-red-500/50 decoration-2">No guesswork.</span>
                                        <span className="block line-through text-gray-600 decoration-red-500/50 decoration-2">No blind decisions.</span>
                                    </p>
                                </div>
                                <div className="md:col-span-2">
                                    <p className="text-2xl font-medium text-emerald-50 leading-relaxed border-l-4 border-emerald-500 pl-6">
                                        Only data-driven, field-specific recommendations designed for <span className="text-transparent bg-clip-text bg-gradient-to-r from-green-400 to-emerald-300">Healthy & Wealthy Agriculture</span>.
                                    </p>
                                </div>
                            </div>

                            <div className="relative z-10 text-center md:text-left">
                                <p className="text-xl text-gray-300 font-light mb-8 max-w-3xl">
                                    FarmGet transforms farming into a smart, measurable, and profitable system — all from one powerful app.
                                </p>

                                <div className="flex flex-col sm:flex-row gap-4 justify-center md:justify-start">
                                    <div className="bg-emerald-500/10 border border-emerald-500/30 px-6 py-4 rounded-xl">
                                        <p className="text-emerald-300 font-bold tracking-wide">Precision in Every Decision.</p>
                                    </div>
                                    <div className="bg-emerald-500/10 border border-emerald-500/30 px-6 py-4 rounded-xl">
                                        <p className="text-emerald-300 font-bold tracking-wide">Performance in Every Harvest.</p>
                                    </div>
                                </div>
                            </div>
                        </div>

                    </motion.div>
                </div>
            </section>
        </div>
    );
};

export default FarmgetApp;
